import { useMemo, useState } from "react";
import { CheckSquare, Loader2, Send } from "@fg/icons";
import { normalizeUserId, type ChatReaction, type MeetingChatMessage } from "@/api/connectApi";
import { useChatMessageAction } from "@/api/hooks";
import { splitFileMarkers } from "@/lib/fileMarkers";
import { cn } from "@/lib/utils";
import { ChatAvatar, timeLabel } from "../chat/chatUi";

/** A question travels as a normal meeting-chat message wrapped in <div data-fg-qna="1">, so it needs no server change. */
export const QNA_ATTR = "data-fg-qna";
const WRAP = /^\s*<div data-fg-qna="1">([\s\S]*)<\/div>\s*$/;
const UP = "👍";
const DONE = "✅";

export const isQuestion = (html: string) => WRAP.test(html);
export const wrapQuestion = (text: string) =>
    `<div ${QNA_ATTR}="1"><p>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</p></div>`;

function plain(html: string): string {
    const inner = WRAP.exec(html)?.[1] ?? html;
    const doc = new DOMParser().parseFromString(inner.replace(/<br\s*\/?>/gi, "\n"), "text/html");
    return (doc.body.textContent ?? "").trim();
}

/**
 * Meeting Q&A, as in Teams: anyone asks a question, others upvote it, and it can be marked answered. Open questions
 * are listed by votes (newest first on a tie); answered ones drop to the bottom. Questions also appear in the chat.
 */
export function QnaPanel({ meetingId, me, messages, reactions, onAsk }: {
    meetingId: string;
    me: string;
    messages: MeetingChatMessage[];
    reactions: ChatReaction[] | undefined;
    onAsk: (html: string) => Promise<void>;
}) {
    const action = useChatMessageAction("meetings", meetingId);
    const [text, setText] = useState("");
    const [asking, setAsking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const rows = useMemo(() => {
        const votes = (id: string) => (reactions ?? []).find((r) => r.messageId === id && r.emoji === UP);
        const done = (id: string) => (reactions ?? []).some((r) => r.messageId === id && r.emoji === DONE && r.count > 0);
        return messages
            .filter((m) => isQuestion(splitFileMarkers(m.bodyHtml).html) || isQuestion(m.bodyHtml))
            .map((m) => ({ m, up: votes(m.id), answered: done(m.id) }))
            .sort((a, b) =>
                Number(a.answered) - Number(b.answered)
                || (b.up?.count ?? 0) - (a.up?.count ?? 0)
                || new Date(b.m.sentAt).getTime() - new Date(a.m.sentAt).getTime());
    }, [messages, reactions]);

    const ask = async () => {
        const q = text.trim();
        if (!q || asking) return;
        setAsking(true);
        setError(null);
        try {
            await onAsk(wrapQuestion(q));
            setText("");
        } catch {
            setError("Couldn't post your question. Try again.");
        } finally {
            setAsking(false);
        }
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col" role="tabpanel" aria-label="Q&A">
            <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3" aria-label="Questions">
                {rows.length === 0 ? (
                    <li className="px-2 py-8 text-center text-sm text-muted-foreground">No questions yet. Ask one below; others can upvote it.</li>
                ) : rows.map(({ m, up, answered }) => {
                    const mine = normalizeUserId(m.authorUserId) === me;
                    const voted = up?.userIds.some((u) => normalizeUserId(u) === me) ?? false;
                    const name = m.authorDisplayName || m.authorEmail || "Someone";
                    return (
                        <li key={m.id} className={cn("rounded-lg border p-3", answered && "opacity-70")}>
                            <div className="flex items-start gap-2">
                                <ChatAvatar name={name} size="sm" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-muted-foreground">{name} · {timeLabel(new Date(m.sentAt))}{answered ? " · Answered" : ""}</p>
                                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{plain(m.bodyHtml)}</p>
                                </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <button
                                    type="button"
                                    aria-pressed={voted}
                                    aria-label={`${voted ? "Remove upvote" : "Upvote"}, ${up?.count ?? 0} votes`}
                                    onClick={() => action.mutate({ type: voted ? "unreact" : "react", messageId: m.id, emoji: UP })}
                                    className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums hover:bg-accent", voted && "border-primary bg-primary/10 text-primary")}
                                >
                                    <span aria-hidden>👍</span> {up?.count ?? 0}
                                </button>
                                {mine || answered ? (
                                    <button
                                        type="button"
                                        onClick={() => action.mutate({ type: answered ? "unreact" : "react", messageId: m.id, emoji: DONE })}
                                        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs hover:bg-accent"
                                    >
                                        <CheckSquare className="h-3.5 w-3.5" aria-hidden /> {answered ? "Reopen" : "Mark answered"}
                                    </button>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
            <div className="shrink-0 border-t p-2">
                {error ? <p role="alert" className="px-1 pb-1 text-xs text-destructive">{error}</p> : null}
                <div className="flex items-end gap-2">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void ask(); } }}
                        rows={2}
                        placeholder="Ask a question"
                        aria-label="Ask a question"
                        className="min-h-[40px] flex-1 resize-none rounded-md border bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                        type="button"
                        onClick={() => void ask()}
                        disabled={!text.trim() || asking}
                        className="flex h-9 items-center gap-1 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-50"
                    >
                        {asking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Ask
                    </button>
                </div>
            </div>
        </div>
    );
}
